import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Fab,
  Stack,
  Toolbar,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  IconButton,
  Chip,
  Divider,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import { CartContext } from "../../store/context/CartContext";
import FeedCard from "../../components/FeedCard/FeedCard";
import { NewsApi } from "../../api/news.api";
import UploadsService from "../../services/uploads.service";
import PostDialog from "../../components/PostDialog/PostDialog";



const emptyForm = {
  id: "",
  thumbnail: "",
  // thumbnailFile: null,
  redirectlink: "",
  description: "",
  order: 0,
  hidden: false,
  note: "",
};

function moveItem(array, fromIndex, toIndex) {
  if (toIndex < 0 || toIndex >= array.length) return array;
  const copy = array.slice();
  const [item] = copy.splice(fromIndex, 1);
  copy.splice(toIndex, 0, item);
  return copy;
}

const Feed = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [publications, setPublications] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const [orderList, setOrderList] = useState([]);
  const [errors, setErrors] = useState({});
  const [severity, setSeverity] = useState('success');
  const { setTitleSeccion } = useContext(CartContext);

  useEffect(() => {
    setTitleSeccion("Feed");
  }, []);


  useEffect(() => {
    (async () => {
      try {
        const list = await NewsApi.list();
        const arr = Array.isArray(list) ? list : [];
        arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setPublications(arr);
        setOrderList(arr);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!reorderMode) {
      setOrderList(
        [...publications].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      );
    }
  }, [publications, reorderMode]);


  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setOpenForm(true);
  };

  const openEdit = (pub) => {
    setEditingId(pub.id);
    setFormData({
      id: pub.id,
      thumbnail: pub.thumbnail || "",
      redirectlink: pub.redirectlink || "",
      description: pub.description || "",
      order: pub.order || 0,
      hidden: !!pub.hidden,
      note: pub.note || "",
    });
    setOpenForm(true);
  };


  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (!formData.description?.trim()) {
        errors.description = "La descripción es obligatoria";
      }
      if (!formData.redirectlink?.trim()) {
        errors.redirectlink = "El link de redirección es obligatorio";
      }
      if (!formData.thumbnail) {
        errors.thumbnail = "La imagen es obligatoria";
      }

      let thumbnailUrl = formData.thumbnail;
      if (formData.thumbnail instanceof File) {
        const presign = await UploadsService.presignNewsImage(
          formData.thumbnail.type || "image/jpeg"
        );

        await UploadsService.putToPresigned(
          presign.url,
          formData.thumbnail,
          presign.headers
        );

        thumbnailUrl = presign.publicUrl;
      }

      const maxOrder = publications.reduce(
        (m, p) => Math.max(m, p.order ?? 0),
        0
      );

      const payload = {
        thumbnail: thumbnailUrl,
        redirectlink: formData.redirectlink,       // si tu backend espera redirectLink, cambialo acá
        description: formData.description ?? "",
        order: editingId
          ? (formData.order ?? 0)
          : (formData.order ?? (maxOrder + 1)),
        hidden: !!formData.hidden,
        note: formData.note ?? null,
      };

      let saved;
      if (editingId) {
        saved = await NewsApi.update(editingId, payload);
        setPublications(prev =>
          prev
            .map(p => (p.id === saved.id ? saved : p))
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        );
      } else {
        console.log("Creating new publication with payload:", payload);
        saved = await NewsApi.create(payload);
        setPublications(prev =>
          [...prev, saved].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        );
      }
      setOpenForm(false);
      setFormData(emptyForm);
      setEditingId(null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await NewsApi.remove(id);
      setPublications((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      console.error(e);
    }
  };

  // 👇 Ahora usa NewsApi.toggleHidden y actualiza el array local
  const handleToggleHidden = async (id, nextHidden) => {
    try {
      const saved = await NewsApi.toggleHidden(id, nextHidden);
      setPublications((prev) =>
        prev.map((p) => (p.id === saved.id ? saved : p))
      );
    } catch (e) {
      console.error(e);
    }
  };

  const idsOriginales = useMemo(() => publications.map((p) => p.id).join(","), [publications]);
  const idsActuales = useMemo(() => orderList.map((p) => p.id).join(","), [orderList]);
  const hayCambios = idsOriginales !== idsActuales;

  const moveUp = (index) => setOrderList((prev) => moveItem(prev, index, index - 1));
  const moveDown = (index) => setOrderList((prev) => moveItem(prev, index, index + 1));

  const guardarNuevoOrden = async () => {
    try {
      const ids = orderList.map((p) => p.id);
      await NewsApi.reorder(ids);
      const reordenadas = orderList.map((p, idx) => ({ ...p, order: idx + 1 }));
      setPublications(reordenadas);
      setReorderMode(false);
    } catch (e) {
      console.error(e);
    }
  };

  const cancelarReorden = () => {
    setOrderList(
      [...publications].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    );
    setReorderMode(false);
  };

  return (
    <Box p={2}>

      <Toolbar disableGutters sx={{ mb: 2, justifyContent: "space-between" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="h6">Publicaciones</Typography>
          {reorderMode && <Chip size="small" color="secondary" label="Modo reordenar" />}
        </Stack>

        <Stack direction="row" spacing={1}>
          {!reorderMode ? (
            <>
              <Button
                startIcon={<SwapVertIcon />}
                variant="outlined"
                onClick={() => setReorderMode(true)}
              >
                Reordenar
              </Button>
              <Fab color="primary" aria-label="add" onClick={openCreate} size="small">
                <AddIcon />
              </Fab>
            </>
          ) : (
            <>
              <Button
                startIcon={<SaveIcon />}
                variant="contained"
                onClick={guardarNuevoOrden}
                disabled={!hayCambios}
              >
                Guardar nuevo orden
              </Button>
              <Button startIcon={<CloseIcon />} variant="text" onClick={cancelarReorden}>
                Cancelar
              </Button>
            </>
          )}
        </Stack>
      </Toolbar>

      {!reorderMode ? (
        <Grid container spacing={1}>
          {publications.map((pub) => (
            <Grid item xs={12} sm={6} md={4} key={pub.id}>
              <FeedCard
                publication={pub}
                onEdit={openEdit}
                onDelete={handleDelete}
                onToggleHidden={handleToggleHidden}
                setOpenForm={setOpenForm}
                setEditingId={setEditingId}
              />
            </Grid>
          ))}
        </Grid>
      ) : (
        <Paper variant="outlined" sx={{ p: 1 }}>
          <List dense disablePadding>
            {orderList.map((pub, idx) => (
              <Box key={pub.id}>
                <ListItem
                  secondaryAction={
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        edge="end"
                        aria-label="up"
                        onClick={() => moveUp(idx)}
                        disabled={idx === 0}
                        size="small"
                      >
                        <ArrowUpwardIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="down"
                        onClick={() => moveDown(idx)}
                        disabled={idx === orderList.length - 1}
                        size="small"
                      >
                        <ArrowDownwardIcon />
                      </IconButton>
                    </Stack>
                  }
                >
                  <ListItemAvatar>
                    <Avatar
                      variant="rounded"
                      src={pub.thumbnail}
                      alt={pub.description}
                      sx={{ width: 56, height: 56 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body1">
                          {idx + 1}. {pub.description?.slice(0, 30) || "Sin descripción"}
                        </Typography>
                        {pub.hidden && <Chip size="small" label="Oculto" color="warning" />}
                      </Stack>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {pub?.redirectlink}
                      </Typography>
                    }
                  />
                </ListItem>
                {idx < orderList.length - 1 && <Divider component="li" />}
              </Box>
            ))}
          </List>
        </Paper>
      )}

      {/* Dialog de crear/editar */}
      <PostDialog
        open={openForm}
        setOpenForm={setOpenForm}
        formData={formData}
        setFormData={setFormData}
        editingId={editingId}
        loading={loading}
        handleSubmit={handleSubmit}
        errors={errors}
        setErrors={setErrors}
      />
    </Box>
  );
};

export default Feed;
